/**
 * `/workflows` — everything this account has standing, in one list (DOP-478).
 *
 * It replaces both `/templates` (where you configured extraction) and the old
 * `/workflow` (where you read one of the outputs). Those were two words for
 * two halves of the same object, and the user had to know which half they
 * wanted before they could navigate. A workflow is the whole thing; this is
 * the list of them, and creating one is a modal over this list rather than a
 * different page.
 *
 * The modal's open state lives in the URL (`?new=1`, with `?template=` naming
 * the step-two composer) rather than in component state, so the `/templates`
 * and `/templates?compose=…` redirects land on exactly the screen their old
 * link meant.
 */

import { Loader2, Plus } from "lucide-react";
import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import DashboardListRow from "../components/Dashboards/DashboardListRow";
import PageTabs from "../components/Dashboards/PageTabs";
import DeleteWorkflowDialog from "../components/Dashboards/DeleteWorkflowDialog";
import NewDashboardModal, {
	isNewDashboardTemplate,
} from "../components/Dashboards/NewDashboardModal";
import {
	type DashboardIndexRow,
	type DashboardKind,
	TYPE_LABELS,
	useDashboardsIndex,
} from "../context/DashboardsIndex";

/** The query keys the "New workflow" modal is opened by. */
export const NEW_DASHBOARD_PARAM = "new";
export const NEW_DASHBOARD_TEMPLATE_PARAM = "template";

const Dashboards: React.FC = () => {
	const { rows, loading, error, refresh, remove } = useDashboardsIndex();
	const [searchParams, setSearchParams] = useSearchParams();

	/**
	 * The workflow the confirm dialog is asking about, if any.
	 *
	 * Held here rather than in the row: the row unmounts the moment the delete
	 * lands, and a dialog owned by it would go with it mid-request, taking the
	 * spinner and any refusal along too.
	 */
	const [pendingDelete, setPendingDelete] = useState<DashboardIndexRow | null>(
		null,
	);
	const [deleting, setDeleting] = useState(false);
	const [deleteError, setDeleteError] = useState<string | null>(null);

	const closeDeleteDialog = () => {
		setPendingDelete(null);
		setDeleteError(null);
	};

	const confirmDelete = async () => {
		if (!pendingDelete) return;
		setDeleting(true);
		setDeleteError(null);
		try {
			await remove(pendingDelete);
			setPendingDelete(null);
		} catch (caught) {
			// Shown in the dialog, which stays open. The refusals that reach here
			// are actionable ("a run is still going"), and closing over them would
			// leave the row in place with no explanation for why.
			setDeleteError(
				caught instanceof Error
					? caught.message
					: "Could not delete this workflow.",
			);
		} finally {
			setDeleting(false);
		}
	};

	/** The kind tab on screen; "all" until one is chosen. */
	const [kind, setKind] = useState<string>("all");
	const kinds = (Object.keys(TYPE_LABELS) as DashboardKind[]).filter((k) =>
		rows.some((row) => row.kind === k),
	);
	const tabs = [
		{ id: "all", label: "All", count: rows.length },
		...kinds.map((k) => ({
			id: k,
			label: TYPE_LABELS[k],
			count: rows.filter((row) => row.kind === k).length,
		})),
	];
	// A kind whose last workflow was deleted falls back to All.
	const shown = kind === "all" || !kinds.includes(kind as DashboardKind)
		? rows
		: rows.filter((row) => row.kind === kind);

	const composing = searchParams.get(NEW_DASHBOARD_PARAM) === "1";
	const requestedTemplate = searchParams.get(NEW_DASHBOARD_TEMPLATE_PARAM);

	const setComposing = (open: boolean) => {
		const next = new URLSearchParams(searchParams);
		if (open) {
			next.set(NEW_DASHBOARD_PARAM, "1");
		} else {
			next.delete(NEW_DASHBOARD_PARAM);
			next.delete(NEW_DASHBOARD_TEMPLATE_PARAM);
		}
		// Opening pushes (so Back closes the modal); closing REPLACES, so the
		// pair does not stack two entries and make Back feel stuck.
		setSearchParams(next, { replace: !open });
	};

	return (
		<div className="flex-1 overflow-y-auto">
			<div className="mx-auto max-w-5xl space-y-6 px-6 py-8">
				<header className="flex items-end justify-between gap-4">
					<div>
						<h1 className="text-2xl font-semibold tracking-[-0.01em] text-[#111111]">Workflows</h1>
						<p className="mt-1 text-sm text-[#5B6472]">
							Everything you have set up. Open one to see its runs and results.
						</p>
					</div>
					<button
						type="button"
						onClick={() => setComposing(true)}
						className="inline-flex h-10 flex-shrink-0 items-center gap-1.5 rounded-[10px] bg-[#004C4B] px-4 text-sm font-semibold text-white hover:bg-[#003a3a]"
					>
						<Plus className="h-4 w-4" aria-hidden />
						New workflow
					</button>
				</header>

				{/*
				 * Alongside the rows, never instead of them: one source failing
				 * says nothing about the two that loaded, and blanking the list
				 * over a monitoring 502 would tell the user their KPI workflows
				 * are gone.
				 */}
				{error && (
					<p role="alert" className="text-sm text-red-700">
						{error}{" "}
						<button
							type="button"
							onClick={refresh}
							className="font-medium text-red-600 underline hover:text-red-500"
						>
							Retry
						</button>
					</p>
				)}

				{loading && rows.length === 0 ? (
					<div className="flex items-center gap-2 text-sm text-text-muted">
						<Loader2 className="h-4 w-4 animate-spin" />
						Loading workflows…
					</div>
				) : rows.length === 0 ? (
					!error && (
						<div className="rounded-xl border border-dashed border-gray-300 bg-white px-6 py-10 text-center">
							<p className="text-sm font-semibold text-[#111111]">Nothing created yet.</p>
							<p className="mt-1 text-sm text-[#5B6472]">
								Pick a template to set up your first workflow.
							</p>
							<button
								type="button"
								onClick={() => setComposing(true)}
								className="mt-4 inline-flex h-9 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3.5 text-sm font-medium text-[#1F2937] hover:bg-gray-50"
							>
								<Plus className="h-4 w-4" aria-hidden />
								New workflow from a template
							</button>
						</div>
					)
				) : (
					<section aria-label="Your workflows">
						{tabs.length > 2 && (
							<PageTabs tabs={tabs} activeId={kind} onSelect={setKind} ariaLabel="Workflow kinds" />
						)}
						<div className="mt-4 rounded-xl border border-gray-200 bg-white" data-testid="workflows-table">
							<div className="flex items-center gap-3 rounded-t-xl border-b border-gray-200 bg-[#fafafa] px-5 py-2.5 text-xs font-semibold text-[#5B6472]">
								<span className="flex-1">Workflow</span>
								<span className="hidden w-56 md:block">Last run</span>
								<span className="w-32">Runs</span>
								<span className="w-9" aria-hidden />
							</div>
							<ul>
								{shown.map((row) => (
									<DashboardListRow
										key={row.key}
										row={row}
										onRequestDelete={setPendingDelete}
									/>
								))}
							</ul>
						</div>
					</section>
				)}
			</div>

			{pendingDelete && (
				<DeleteWorkflowDialog
					row={pendingDelete}
					deleting={deleting}
					error={deleteError}
					onCancel={closeDeleteDialog}
					onConfirm={() => void confirmDelete()}
				/>
			)}

			{composing && (
				<NewDashboardModal
					initialTemplate={
						isNewDashboardTemplate(requestedTemplate)
							? requestedTemplate
							: null
					}
					onClose={() => setComposing(false)}
					onCreated={refresh}
				/>
			)}
		</div>
	);
};

export default Dashboards;
